import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatINR } from "../lib/format";
import { Link } from "react-router-dom";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function EmiCalendar() {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        api.get("/calendar/emi", { params: { year, month } }).then((r) => setData(r.data));
        setSelected(null);
    }, [year, month]);

    const map = (data?.days || []).reduce((acc, d) => {
        acc[d.date] = d;
        return acc;
    }, {});

    function nav(delta) {
        let m = month + delta;
        let y = year;
        if (m < 1) { m = 12; y -= 1; }
        else if (m > 12) { m = 1; y += 1; }
        setMonth(m);
        setYear(y);
    }

    // Build calendar grid
    const firstDay = new Date(year, month - 1, 1);
    const startDow = firstDay.getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
        cells.push({ day: d, iso, info: map[iso] });
    }

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">EMI Calendar</h1>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="h-10 rounded-xl" onClick={() => nav(-1)} data-testid="cal-prev">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="px-3 py-2 bg-white rounded-xl border border-slate-100 font-display font-bold text-slate-900 min-w-[140px] text-center">
                        {MONTHS[month - 1]} {year}
                    </div>
                    <Button variant="outline" className="h-10 rounded-xl" onClick={() => nav(1)} data-testid="cal-next">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <Card className="rounded-2xl border-slate-100 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        Month total scheduled
                    </div>
                    <div className="num font-bold text-blue-900 text-lg" data-testid="cal-month-total">
                        {data ? formatINR(data.month_total) : "—"}
                    </div>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center mb-2">
                    {DAYS.map((d) => (
                        <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold py-1">{d}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {cells.map((c, i) =>
                        c === null ? (
                            <div key={`blank-${i}`} />
                        ) : (
                            <button
                                key={c.iso}
                                data-testid={`cal-day-${c.iso}`}
                                onClick={() => c.info && setSelected(c)}
                                className={`aspect-square rounded-lg p-1.5 text-left flex flex-col justify-between transition ${
                                    c.info
                                        ? "bg-blue-50 hover:bg-blue-100 border border-blue-100"
                                        : "bg-slate-50 border border-transparent"
                                } ${selected?.iso === c.iso ? "ring-2 ring-blue-900" : ""}`}
                            >
                                <div className="text-xs font-semibold text-slate-700">{c.day}</div>
                                {c.info && (
                                    <div className="text-right">
                                        <div className="text-[10px] font-bold text-blue-900 num">
                                            {formatINR(c.info.total).replace("₹ ", "₹")}
                                        </div>
                                        <div className="text-[9px] text-slate-500">{c.info.items.length} EMI</div>
                                    </div>
                                )}
                            </button>
                        )
                    )}
                </div>
            </Card>

            {selected?.info && (
                <Card className="rounded-2xl border-slate-100">
                    <div className="p-5 pb-2 flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold text-slate-900">
                            EMIs due on {selected.iso}
                        </h2>
                        <div className="num font-bold text-blue-900">{formatINR(selected.info.total)}</div>
                    </div>
                    <div className="divide-y divide-slate-100">
                        {selected.info.items.map((it) => (
                            <Link
                                key={it.loan_id}
                                to={`/loans/${it.loan_id}`}
                                className="p-4 flex items-center justify-between hover:bg-slate-50"
                                data-testid={`cal-item-${it.loan_id}`}
                            >
                                <div>
                                    <div className="font-semibold text-slate-900">{it.customer_name}</div>
                                    <div className="text-xs text-slate-500">
                                        {it.customer_code} · {it.loan_code} · {it.customer_mobile}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="num font-bold text-slate-900">{formatINR(it.interest)}</div>
                                    <div className="text-[11px] text-slate-500">
                                        interest due
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}
